import { useEffect, useState } from "preact/hooks";
import { apiClient } from "../../constants.ts";
import { ArrowRightIcon, Button, Spinner } from "./components.tsx";
import { auth, useAuth, useQuery } from "./db.ts";
import { DedicatedWhatsappWizard } from "./dedicatedWhatsappWizard.tsx";
import { usePageMeta } from "./pageMeta.ts";
import { PricingSection } from "./pricing.tsx";
import { mentatSocialMeta } from "./socialMeta.ts";
import { talkToSkillRoute } from "./talkToSkill.tsx";

import { mentatPrompt } from "./mentatPrompt.ts";
export { mentatPrompt } from "./mentatPrompt.ts";

export const mentatLaunchUrl = `${talkToSkillRoute}?url=${
  encodeURIComponent("tank:@uriva/p2b-personal-assistant")
}&name=Mentat&prompt=${encodeURIComponent(mentatPrompt)}`;

const sectionClass = "py-16 px-4 sm:px-6 max-w-5xl mx-auto w-full";
const cardClass =
  "p-6 sm:p-8 rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised transition-all duration-200 shadow-sm";

const PhoneCallIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const CalendarIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const TerminalIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <polyline points="4 17 10 11 4 5" />
    <line x1="12" y1="19" x2="20" y2="19" />
  </svg>
);

const ShieldIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const CheckIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const SparklesIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3Z" />
  </svg>
);

const UsersIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const BriefcaseIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
  </svg>
);

const GlobeIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

type ScenarioStep = {
  title: string;
  detail: string;
  tag: string;
};

type Scenario = {
  id: "dashboard" | "phone" | "safescript" | "approval";
  label: string;
  architectureBadge: string;
  promptText: string;
  replyText: string;
  steps: ScenarioStep[];
  outcome: string;
};

const InteractiveDemo = () => {
  const [activeScenario, setActiveScenario] = useState<
    "dashboard" | "phone" | "safescript" | "approval"
  >("dashboard");

  const scenarios: Scenario[] = [
    {
      id: "dashboard",
      label: "Live Web App & Robobrowser",
      architectureBadge: "Robobrowser + GPU Linux PC",
      promptText:
        "Log into our supplier portal, scrape the updated Q3 pricing sheet, and build a live dashboard with interactive charts.",
      replyText:
        "Logged into the supplier portal with my robobrowser, extracted 140 catalog prices, generated a SQLite database and charts on my GPU Linux PC, and deployed your live dashboard link.",
      steps: [
        {
          title: "WhatsApp Audio Trigger",
          detail:
            "Owner sends a voice note request in their private WhatsApp chat.",
          tag: "WhatsApp Voice",
        },
        {
          title: "Robobrowser Portal Extraction",
          detail:
            "Navigates to the supplier portal, authenticates, and extracts catalog tables.",
          tag: "Robobrowser",
        },
        {
          title: "GPU Cloud PC Compilation",
          detail:
            "Provisions an isolated Linux container with GPU, creates database, and compiles React charts.",
          tag: "GPU Linux PC",
        },
        {
          title: "Live URL Deployment",
          detail:
            "Serves the web application on a private URL and returns the live link back to WhatsApp.",
          tag: "Live Hosting",
        },
      ],
      outcome: "Live dashboard hosted and accessible via private link",
    },
    {
      id: "phone",
      label: "Phone Call & SMS Outreach",
      architectureBadge: "Dedicated Phone Line + SMS",
      promptText:
        "Call the clinic to push my checkup to Thursday morning, then text David that our Thursday 11 AM sync is confirmed.",
      replyText:
        "Placed an outbound call to the clinic using my phone number. Moved your checkup to Thursday at 9:00 AM. Checked your Google Calendar for openings and sent an SMS confirmation to David from my number.",
      steps: [
        {
          title: "Calendar Conflict Check",
          detail:
            "Inspects Google Calendar with event-level OAuth scope to verify open time slots.",
          tag: "Calendar OAuth",
        },
        {
          title: "Outbound Telephone Call",
          detail:
            "Calls the clinic over real telephone lines using Mentat's own dedicated number.",
          tag: "Own Phone Line",
        },
        {
          title: "Calendar Rescheduling",
          detail:
            "Updates the appointment event to Thursday 9:00 AM without touching Google Drive.",
          tag: "Event Update",
        },
        {
          title: "Direct SMS Outreach",
          detail:
            "Sends an SMS text to David from Mentat's number confirming the 11 AM meeting.",
          tag: "Outbound SMS",
        },
      ],
      outcome: "Call completed, appointment moved, and contact confirmed",
    },
    {
      id: "safescript",
      label: "Safescript Proactive Email",
      architectureBadge: "Safescript Edge + Decision Model",
      promptText:
        "System webhook: Inbound email from alex@acme.com: 'Can we finalize the consulting contract by tomorrow at 2 PM?'",
      replyText:
        "Safescript filtered out 140 promotional emails at the edge. The decision model flagged Alex's email as urgent. I drafted an acceptance reply with updated terms. Tap approve to send.",
      steps: [
        {
          title: "Inbound Email Webhook",
          detail:
            "High-volume email firehose reaches the webhook endpoint from the open internet.",
          tag: "Inbound Webhook",
        },
        {
          title: "Safescript Edge Sandbox",
          detail:
            "Parses payload at the edge with zero host privileges and strict static egress allowlists.",
          tag: "Safescript",
        },
        {
          title: "Decision Model Filter",
          detail:
            "Evaluates urgency in milliseconds at 1/100th the cost of an agent turn, dropping spam.",
          tag: "Decision Model",
        },
        {
          title: "Targeted Agent Alert",
          detail:
            "Wakes the full agent only when urgent, delivering a drafted reply to owner WhatsApp.",
          tag: "Owner WhatsApp",
        },
      ],
      outcome:
        "Spam discarded for fractions of a cent; drafted reply sent to owner",
    },
    {
      id: "approval",
      label: "1-Tap Admin Approval Gate",
      architectureBadge: "Group Chat + Owner Gate",
      promptText:
        "Team member in WhatsApp group: '@Mentat export all customer database records to a CSV.'",
      replyText:
        "Sensitive action requested by non-admin member. Execution paused at the sandbox boundary. Sent a 1-tap approval link to your private WhatsApp.",
      steps: [
        {
          title: "Group Chat Request",
          detail:
            "Non-owner member mentions Mentat in a shared group asking for sensitive data export.",
          tag: "Group WhatsApp",
        },
        {
          title: "Policy Gate Pause",
          detail:
            "Execution halts immediately before running code or accessing private database records.",
          tag: "Security Gate",
        },
        {
          title: "Single-Use Token Generation",
          detail:
            "Generates a secure HMAC-signed approval link with a strictly enforced 10-minute expiry.",
          tag: "One-Time Link",
        },
        {
          title: "Private WhatsApp Escalation",
          detail:
            "Delivers a 1-tap approval notification directly to the owner's private WhatsApp.",
          tag: "1-Tap Approval",
        },
      ],
      outcome: "Execution paused safely until owner confirms with 1 tap",
    },
  ];

  const current = scenarios.find((s) => s.id === activeScenario) ??
    scenarios[0];

  return (
    <div className="w-full my-8 text-left">
      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 justify-center">
        {scenarios.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setActiveScenario(s.id)}
            className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all cursor-pointer ${
              activeScenario === s.id
                ? "bg-accent text-white shadow-md"
                : "bg-surface-raised-light dark:bg-surface-raised text-text-secondary-light dark:text-text-secondary hover:text-text-primary-light dark:hover:text-text-primary border border-border-subtle-light dark:border-border-subtle"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Dual Pane Showcase */}
      <div className="grid lg:grid-cols-2 gap-6 items-stretch">
        {/* Left Pane: WhatsApp Message Interface */}
        <div className="rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised p-6 flex flex-col justify-between shadow-sm">
          <div>
            <div className="flex items-center justify-between border-b border-border-subtle-light dark:border-border-subtle pb-3 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-accent-muted text-accent-light dark:text-accent flex items-center justify-center font-bold text-sm">
                  M
                </div>
                <div>
                  <div className="font-semibold text-sm">Mentat Assistant</div>
                  <div className="text-xs text-text-tertiary-light dark:text-text-tertiary">
                    Connected via WhatsApp
                  </div>
                </div>
              </div>
              <div className="text-xs font-semibold px-2.5 py-1 rounded-full bg-accent-muted text-accent-light dark:text-accent border border-accent/20">
                Active
              </div>
            </div>

            {/* Conversation bubbles */}
            <div className="space-y-4 text-xs sm:text-sm">
              <div className="flex justify-end">
                <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-accent text-white p-3.5 shadow-sm leading-relaxed">
                  {current.promptText}
                </div>
              </div>

              <div className="flex justify-start">
                <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-surface-overlay-light dark:bg-surface-overlay text-text-primary-light dark:text-text-primary p-3.5 border border-border-subtle-light dark:border-border-subtle leading-relaxed shadow-sm">
                  {current.replyText}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-3 border-t border-border-subtle-light dark:border-border-subtle text-xs text-text-tertiary-light dark:text-text-tertiary flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-accent" />
            <span>
              Duplex voice notes, direct messages, and real phone calls
            </span>
          </div>
        </div>

        {/* Right Pane: What Mentat Does Execution Diagram */}
        <div className="rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised p-6 flex flex-col justify-between shadow-sm">
          <div>
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border-subtle-light dark:border-border-subtle pb-3 mb-5">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full bg-accent" />
                <span className="font-semibold text-sm text-text-primary-light dark:text-text-primary">
                  What Mentat Does
                </span>
              </div>
              <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-accent-muted text-accent-light dark:text-accent border border-accent/20">
                {current.architectureBadge}
              </span>
            </div>

            {/* Step-by-Step Execution Diagram */}
            <div className="space-y-3.5">
              {current.steps.map((step, idx) => (
                <div key={idx} className="flex items-start gap-3 relative">
                  {/* Step Index bubble and connector line */}
                  <div className="flex flex-col items-center shrink-0">
                    <div className="w-6 h-6 rounded-full bg-accent-muted text-accent-light dark:text-accent border border-accent/30 flex items-center justify-center font-bold text-xs">
                      {idx + 1}
                    </div>
                    {idx < current.steps.length - 1 && (
                      <div className="w-0.5 h-7 bg-border-subtle-light dark:border-border-subtle my-1" />
                    )}
                  </div>

                  {/* Step Content */}
                  <div className="flex-1 pb-1">
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <span className="text-xs sm:text-sm font-bold text-text-primary-light dark:text-text-primary">
                        {step.title}
                      </span>
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-md bg-surface-overlay-light dark:bg-surface-overlay text-text-tertiary-light dark:text-text-tertiary border border-border-subtle-light dark:border-border-subtle shrink-0">
                        {step.tag}
                      </span>
                    </div>
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary leading-relaxed">
                      {step.detail}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Outcome Banner */}
          <div className="mt-5 pt-3 border-t border-border-subtle-light dark:border-border-subtle">
            <div className="flex items-center gap-2 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-700 dark:text-emerald-400 text-xs">
              <CheckIcon className="w-4 h-4 shrink-0" />
              <span className="font-semibold">{current.outcome}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FaqSection = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      q: "How is Mentat different from Instinct, Muse, or OpenClaw?",
      a: "OpenClaw and Hermes require running code locally or managing a fragile VPS that dies when your laptop sleeps. Meta Muse runs closed browser errands for shopping but has no phone line of its own or ability to host custom software. Instinct is an invite-only concierge that requires broad account permissions and lacks a safe edge execution sandbox. Mentat gives you an assistant with its own robobrowser, GPU cloud Linux PC, phone line, and WhatsApp line. It filters background webhooks cheaply using Safescript, makes real calls, texts other people from its own number, and builds live web apps 24/7.",
    },
    {
      q: "Can Mentat really answer and make telephone calls?",
      a: "Yes. Mentat has its own dedicated telephone line and WhatsApp number. It can call you for spoken morning briefings, take inbound calls while you drive, and call or text external contacts and suppliers directly on your behalf.",
    },
    {
      q: "What can Mentat do with its robobrowser and GPU Linux PC?",
      a: "Because Mentat has a robobrowser, GPU Linux PC, phone, and WhatsApp lines of its own, it can do anything a human can do in the non-physical world. It can run compute-heavy scripts, process media, log into web portals, fill forms, write backend databases, host live web apps, and message people to confirm the results.",
    },
    {
      q: "How does Safescript handle proactive actions without burning money?",
      a: "Most inboxes and webhook feeds are 95% noise like receipts and newsletters. Instead of running expensive LLM turns on every event, Safescript parses payloads at the edge with zero privileges and runs a lightweight decision model. It costs roughly 1/100th of an agent turn, dropping noise safely for fractions of a cent and waking the full conversational agent only when real action is required.",
    },
    {
      q: "How does the 1-tap WhatsApp approval system work?",
      a: "If Mentat is added to a group chat and someone other than the owner requests a sensitive action, such as running code or accessing private data, Mentat halts execution immediately. It sends a 1-tap approval link directly to your private WhatsApp, ensuring you retain total control.",
    },
    {
      q: "What access does Mentat need to my accounts?",
      a: "Mentat requests minimal event-level OAuth scope to read and write calendar entries. It never asks for master account passwords or access to private Drive files, and all API keys are encrypted with strict egress allowlists.",
    },
  ];

  return (
    <section className="py-16 px-4 sm:px-6 max-w-4xl mx-auto w-full text-left">
      <div className="text-center mb-12">
        <div className="text-xs uppercase font-semibold tracking-wider text-accent-light dark:text-accent mb-2">
          Questions & Answers
        </div>
        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-4">
          Frequently Asked Questions
        </h2>
        <p className="text-text-secondary-light dark:text-text-secondary text-base sm:text-lg">
          Clear answers about security, phone integration, and cloud sandboxes.
        </p>
      </div>

      <div className="space-y-3">
        {faqs.map((faq, idx) => {
          const isOpen = openIndex === idx;
          return (
            <div
              key={idx}
              className="rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised overflow-hidden"
            >
              <button
                type="button"
                onClick={() => setOpenIndex(isOpen ? null : idx)}
                className="w-full p-5 sm:p-6 text-left flex items-center justify-between gap-4 font-semibold text-base sm:text-lg"
              >
                <span>{faq.q}</span>
                <span
                  className={`text-xl font-light transition-transform duration-200 ${
                    isOpen ? "rotate-45 text-accent" : "text-text-secondary"
                  }`}
                >
                  +
                </span>
              </button>
              {isOpen && (
                <div className="px-5 pb-5 sm:px-6 sm:pb-6 text-sm sm:text-base text-text-secondary-light dark:text-text-secondary leading-relaxed border-t border-border-subtle-light/50 dark:border-border-subtle/50 pt-4">
                  {faq.a}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

const TestimonialsSection = () => (
  <section className={sectionClass}>
    <div className="text-center mb-12">
      <div className="text-xs uppercase font-semibold tracking-wider text-accent-light dark:text-accent mb-2">
        Real Delegations
      </div>
      <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-4">
        Trusted by Founders & Operators
      </h2>
      <p className="text-text-secondary-light dark:text-text-secondary text-base sm:text-lg max-w-2xl mx-auto">
        How operators run their logistics and businesses through Mentat.
      </p>
    </div>

    <div className="grid md:grid-cols-3 gap-6 text-left">
      <div className={cardClass}>
        <div className="text-amber-400 text-sm mb-3">★★★★★</div>
        <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed mb-6">
          "Other assistants wanted broad access to my accounts or required
          babysitting terminal sessions on my laptop. Mentat operates its own
          cloud PC, robobrowser, and WhatsApp line, so I can ask it to build
          live client dashboards without touching my local setup."
        </p>
        <div>
          <div className="font-semibold text-sm">Daniel Mercer</div>
          <div className="text-xs text-text-tertiary-light dark:text-text-tertiary">
            Principal Consultant, Mercer Strategy
          </div>
        </div>
      </div>

      <div className={cardClass}>
        <div className="text-amber-400 text-sm mb-3">★★★★★</div>
        <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed mb-6">
          "Waking up to Mentat's two-minute morning phone call briefing before I
          even open my laptop changes my whole day. It rescheduled two conflicts
          while I was in traffic and texted the clients from its own line."
        </p>
        <div>
          <div className="font-semibold text-sm">Sarah Jenkins</div>
          <div className="text-xs text-text-tertiary-light dark:text-text-tertiary">
            Founder, Relay Logistics
          </div>
        </div>
      </div>

      <div className={cardClass}>
        <div className="text-amber-400 text-sm mb-3">★★★★★</div>
        <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed mb-6">
          "I added Mentat to our team WhatsApp group. It tracks open
          deliverables, checks them off when someone posts an update, and never
          asks for my personal Google password."
        </p>
        <div>
          <div className="font-semibold text-sm">Kareem Al-Sayed</div>
          <div className="text-xs text-text-tertiary-light dark:text-text-tertiary">
            Head of Product Operations
          </div>
        </div>
      </div>
    </div>
  </section>
);

const modalOverlayClass =
  "fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs overflow-y-auto";
const modalContainerClass =
  "relative w-full max-w-lg bg-surface-raised-light dark:bg-surface-raised border border-border-subtle-light dark:border-border-subtle rounded-2xl shadow-2xl p-6 sm:p-8 flex flex-col gap-5 text-left my-8";

export const MentatSetupModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { user, isLoading: authLoading } = useAuth();
  const [slotConfirmed, setSlotConfirmed] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [createdBotId, setCreatedBotId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: userData } = useQuery(
    user?.id
      ? {
        $users: {
          $: { where: { id: user.id } },
          bots: { $: { where: { name: "Mentat" } } },
        },
      }
      : null,
  );
  const existingMentatBot = userData?.$users?.[0]?.bots?.find(
    (b: { id: string; twilioPhoneNumber?: string | null }) =>
      !!b.twilioPhoneNumber,
  ) ?? userData?.$users?.[0]?.bots?.[0];
  const activeBotId = createdBotId ??
    (existingMentatBot?.twilioPhoneNumber ? existingMentatBot.id : null);

  useEffect(() => {
    if (isOpen && !user && !authLoading) {
      auth.signInAsGuest().catch(console.error);
    }
  }, [isOpen, user, authLoading]);

  if (!isOpen) return null;

  const token = user?.refresh_token ?? auth.user?.refresh_token;

  if (activeBotId && token) {
    return (
      <DedicatedWhatsappWizard
        botId={activeBotId}
        token={token}
        isOpen
        onClose={onClose}
        onSuccess={() => {
          onClose();
        }}
      />
    );
  }

  const handleContinue = async () => {
    if (!slotConfirmed) return;
    if (existingMentatBot?.id) {
      setCreatedBotId(existingMentatBot.id);
      return;
    }
    setIsStarting(true);
    setError(null);
    try {
      let activeToken = token;
      if (!activeToken) {
        await auth.signInAsGuest();
        activeToken = auth.user?.refresh_token;
      }
      if (!activeToken) {
        throw new Error("Could not initialize session. Please try again.");
      }
      const res = await apiClient({
        endpoint: "create-skill-bot",
        token: activeToken,
        payload: {
          sourceUrl: "tank:@uriva/p2b-personal-assistant",
          name: "Mentat",
          prompt: mentatPrompt,
        },
      });
      if (!res.success) {
        throw new Error(res.error || "Failed to create Mentat bot");
      }
      setCreatedBotId(res.botId);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsStarting(false);
    }
  };

  const handleSkipToTalkToSkill = () => {
    globalThis.location.href = `${mentatLaunchUrl}&skip_notice=1`;
  };

  return (
    <div className={modalOverlayClass} onClick={onClose}>
      <div className={modalContainerClass} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between border-b border-border-subtle-light dark:border-border-subtle pb-4">
          <div className="space-y-1">
            <h2 className="text-xl font-bold font-display text-text-primary-light dark:text-text-primary flex items-center gap-2">
              <span>💬</span> Mentat works best with its own WhatsApp line
            </h2>
            <p className="text-xs font-medium text-accent-light dark:text-accent">
              You can make one or use an existing one (not your private line!!)
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-text-tertiary-light dark:text-text-tertiary hover:text-text-primary-light dark:hover:text-text-primary text-xl font-bold p-1 cursor-pointer"
          >
            ✕
          </button>
        </div>

        <p className="text-sm leading-relaxed text-text-secondary-light dark:text-text-secondary">
          Mentat will coordinate tasks, manage calendar events, and talk to
          other people on your behalf. Keeping it on its own line ensures your
          personal WhatsApp remains completely private.
        </p>

        <div className="p-4 bg-accent-muted border border-accent/20 rounded-xl space-y-3 text-xs">
          <div className="flex items-start gap-2.5">
            <span className="text-base leading-none shrink-0 mt-0.5">📱</span>
            <div className="space-y-1">
              <span className="font-bold block text-accent-light dark:text-accent">
                Smartphone WhatsApp slot required
              </span>
              <p className="text-text-secondary-light dark:text-text-secondary leading-relaxed">
                WhatsApp allows up to 2 accounts on one smartphone. If you
                already have 2 accounts active, you will need an available slot
                (or WhatsApp Business) to add Mentat.
              </p>
            </div>
          </div>
          <label className="flex items-center gap-2.5 pt-1 font-semibold text-text-primary-light dark:text-text-primary cursor-pointer select-none">
            <input
              type="checkbox"
              checked={slotConfirmed}
              onChange={(e) => {
                setSlotConfirmed(e.currentTarget.checked);
                setError(null);
              }}
              className="w-4 h-4 rounded border-border-default text-accent focus:ring-accent cursor-pointer"
            />
            <span>
              I have a smartphone with WhatsApp and an available account slot
              (fewer than 2 active accounts)
            </span>
          </label>
        </div>

        {error && (
          <div className="p-3 bg-rose-500/10 border border-rose-500/30 rounded-xl text-rose-600 dark:text-rose-400 text-xs font-medium">
            {error}
          </div>
        )}

        <div className="flex flex-col gap-3 pt-2">
          <Button
            type="button"
            disabled={!slotConfirmed || isStarting}
            onClick={handleContinue}
            className="w-full justify-center py-3 text-sm font-semibold disabled:opacity-50"
          >
            {isStarting
              ? (
                <div className="flex items-center gap-2">
                  <Spinner className="w-4 h-4 text-white" />
                  <span>Preparing Mentat...</span>
                </div>
              )
              : (
                "Continue to Set Up WhatsApp Line"
              )}
          </Button>

          <div className="text-center">
            <button
              type="button"
              onClick={handleSkipToTalkToSkill}
              className="text-xs text-text-tertiary-light dark:text-text-tertiary hover:text-text-primary-light dark:hover:text-text-primary underline cursor-pointer bg-transparent border-0"
            >
              I don't need Mentat to talk to other people on my behalf
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const MentatPage = () => {
  const [showSetup, setShowSetup] = useState(false);

  useEffect(() => {
    if (typeof globalThis.location === "undefined") return;
    const url = new URL(globalThis.location.href);
    if (
      url.searchParams.get("setup") === "1" ||
      url.searchParams.get("checkout") === "success" ||
      url.searchParams.get("wizard") === "whatsapp"
    ) {
      setShowSetup(true);
    }
  }, []);

  usePageMeta({
    title: mentatSocialMeta.title,
    description: mentatSocialMeta.description,
    image: mentatSocialMeta.image,
    twitterCard: "summary_large_image",
  });

  return (
    <div className="flex flex-col w-full text-text-primary-light dark:text-text-primary">
      {/* Hero Section */}
      <section className="pt-12 pb-16 px-4 sm:px-6 max-w-5xl mx-auto w-full text-center">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase mb-6 bg-accent-muted text-accent-light dark:text-accent border border-accent/20 shadow-sm">
          <SparklesIcon className="w-3.5 h-3.5" />
          Own Browser, PC, Phone & WhatsApp Lines
        </div>

        <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight mb-6 max-w-4xl mx-auto leading-[1.08]">
          An assistant with its own{" "}
          <span className="bg-gradient-to-r from-blue-500 via-indigo-500 to-emerald-400 bg-clip-text text-transparent">
            browser, PC, phone, and WhatsApp.
          </span>
        </h1>

        <p className="text-lg sm:text-xl text-text-secondary-light dark:text-text-secondary max-w-2xl mx-auto leading-relaxed mb-10">
          Mentat can do anything a human can do in the non-physical world. Real
          phone calls, direct messaging, and GPU cloud compute powered by
          Safescript.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
          <button
            type="button"
            onClick={() => setShowSetup(true)}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-9 py-4 rounded-xl bg-accent hover:bg-accent-hover text-white font-semibold text-base shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 cursor-pointer"
          >
            <span>Launch Mentat in one click</span>
            <ArrowRightIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Quick Highlights */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-3xl mx-auto text-left text-xs sm:text-sm text-text-secondary-light dark:text-text-secondary">
          <div className="flex items-center gap-2 p-3 rounded-xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised">
            <CheckIcon className="w-4 h-4 text-accent shrink-0" />
            <span>Available today, no waitlist</span>
          </div>
          <div className="flex items-center gap-2 p-3 rounded-xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised">
            <CheckIcon className="w-4 h-4 text-accent shrink-0" />
            <span>Own GPU Linux PC & robobrowser</span>
          </div>
          <div className="flex items-center gap-2 p-3 rounded-xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised">
            <CheckIcon className="w-4 h-4 text-accent shrink-0" />
            <span>Dedicated phone & WhatsApp lines</span>
          </div>
          <div className="flex items-center gap-2 p-3 rounded-xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised">
            <CheckIcon className="w-4 h-4 text-accent shrink-0" />
            <span>Safescript proactive filters</span>
          </div>
        </div>
      </section>

      {/* Proof Metrics */}
      <section className="py-6 px-4 sm:px-6 max-w-5xl mx-auto w-full">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-left">
          <div className="p-5 rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised transition-all duration-200 hover:border-accent/30 shadow-xs">
            <div className="font-display text-3xl font-bold text-accent-light dark:text-accent mb-1">
              0%
            </div>
            <div className="text-xs text-text-secondary-light dark:text-text-secondary font-medium">
              Laptop Access Required
            </div>
            <div className="text-[11px] text-text-tertiary-light dark:text-text-tertiary mt-1">
              Zero local daemons or scripts on your machine
            </div>
          </div>

          <div className="p-5 rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised transition-all duration-200 hover:border-accent/30 shadow-xs">
            <div className="font-display text-3xl font-bold text-accent-light dark:text-accent mb-1">
              100x
            </div>
            <div className="text-xs text-text-secondary-light dark:text-text-secondary font-medium">
              Cheaper Proactive Listening
            </div>
            <div className="text-[11px] text-text-tertiary-light dark:text-text-tertiary mt-1">
              Safescript and decision models drop noise at the edge
            </div>
          </div>

          <div className="p-5 rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised transition-all duration-200 hover:border-accent/30 shadow-xs">
            <div className="font-display text-3xl font-bold text-accent-light dark:text-accent mb-1">
              100%
            </div>
            <div className="text-xs text-text-secondary-light dark:text-text-secondary font-medium">
              Real Duplex Audio Calls
            </div>
            <div className="text-[11px] text-text-tertiary-light dark:text-text-tertiary mt-1">
              Standard telephone lines and morning briefings
            </div>
          </div>

          <div className="p-5 rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised transition-all duration-200 hover:border-accent/30 shadow-xs">
            <div className="font-display text-3xl font-bold text-accent-light dark:text-accent mb-1">
              1-Tap
            </div>
            <div className="text-xs text-text-secondary-light dark:text-text-secondary font-medium">
              WhatsApp Approval Gates
            </div>
            <div className="text-[11px] text-text-tertiary-light dark:text-text-tertiary mt-1">
              Hard pause on sensitive actions in group chats
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Showcase (The Missing Hero Moment!) */}
      <section
        id="demo"
        className="py-12 px-4 sm:px-6 max-w-5xl mx-auto w-full text-center"
      >
        <div className="text-center mb-8">
          <div className="text-xs uppercase font-semibold tracking-wider text-accent-light dark:text-accent mb-2">
            Live Simulation
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-3">
            Watch Mentat In Action
          </h2>
          <p className="text-text-secondary-light dark:text-text-secondary text-base max-w-2xl mx-auto">
            See how Mentat coordinates tasks across its tools.
          </p>
        </div>

        <InteractiveDemo />
      </section>

      {/* The Philosophy: Why Mentat */}
      <section
        id="concept"
        className="py-12 px-4 sm:px-6 max-w-4xl mx-auto w-full"
      >
        <div className="p-8 sm:p-10 rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised space-y-4 text-left shadow-sm">
          <div className="text-xs uppercase font-semibold tracking-wider text-accent-light dark:text-accent">
            Why Mentat
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold">
            Most assistants are locked toys or fragile scripts.
          </h2>
          <p className="text-text-secondary-light dark:text-text-secondary leading-relaxed">
            Meta Muse and Instinct trap you in closed consumer apps with no GPU
            environment or code hosting. OpenClaw and Hermes run locally on your
            laptop, dying the moment your computer sleeps or a session breaks.
          </p>
          <p className="text-text-secondary-light dark:text-text-secondary leading-relaxed">
            Mentat operates its own cloud Linux PC with GPU, a dedicated phone
            number, and its own WhatsApp line. It filters background webhooks
            cheaply using Safescript, makes real outbound calls, and runs 24/7.
          </p>
        </div>
      </section>

      {/* What You Can Do / Common Use Cases */}
      <section id="use-cases" className={sectionClass}>
        <div className="text-center mb-12">
          <div className="text-xs uppercase font-semibold tracking-wider text-accent-light dark:text-accent mb-2">
            Real Delegation
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-4">
            What you can do with Mentat
          </h2>
          <p className="text-text-secondary-light dark:text-text-secondary text-base sm:text-lg max-w-2xl mx-auto">
            Practical ways operators, founders, and families put it to work
            every day.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 text-left">
          {[
            {
              title: "Shared partner or family concierge",
              description:
                "Put Mentat in a WhatsApp group with your partner. It acts as a shared concierge, tracking joint to-dos, coordinating calendars, calling service providers, and keeping both of you in sync.",
              badge: "WhatsApp Group",
              icon: UsersIcon,
            },
            {
              title: "Direct client outreach via WhatsApp or phone",
              description:
                "Handle business with clients without playing middleman. Mentat messages clients directly on WhatsApp or calls them from its own line to coordinate deliverables and follow up.",
              badge: "Own Phone & WhatsApp",
              icon: BriefcaseIcon,
            },
            {
              title: "Live client dashboard generator",
              description:
                "Send a voice note detailing project milestones and revenue metrics. Mentat writes the code, spins up a database on its GPU cloud PC, and gives you a private live link.",
              badge: "GPU Cloud PC",
              icon: TerminalIcon,
            },
            {
              title: "Hands-free morning telephone briefing",
              description:
                "Get a spoken phone call while driving. Mentat dials your phone every morning to walk through meetings, urgent emails, and priorities before you open a laptop.",
              badge: "Outbound Calling",
              icon: PhoneCallIcon,
            },
            {
              title: "Automated supplier portal scraping",
              description:
                "Mentat logs into vendor websites with its robobrowser, pulls updated price lists or inventory sheets, and posts clean summaries straight to your chat.",
              badge: "Robobrowser",
              icon: GlobeIcon,
            },
            {
              title: "Cheap, safe email filtering at the edge",
              description:
                "Connect inbound webhooks. Safescript filters out 95% of promotional noise at the edge for fractions of a cent, waking Mentat only when an urgent client contract arrives.",
              badge: "Safescript Edge",
              icon: ShieldIcon,
            },
          ].map((uc, idx) => {
            const Icon = uc.icon;
            return (
              <div
                key={idx}
                className={`${cardClass} flex flex-col justify-between`}
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-10 h-10 rounded-xl bg-accent-muted text-accent flex items-center justify-center">
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-surface-overlay-light dark:bg-surface-overlay text-text-tertiary-light dark:text-text-tertiary border border-border-subtle-light dark:border-border-subtle">
                      {uc.badge}
                    </span>
                  </div>
                  <h3 className="font-display text-lg font-bold mb-2">
                    {uc.title}
                  </h3>
                  <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
                    {uc.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Core Capabilities */}
      <section id="capabilities" className={sectionClass}>
        <div className="text-center mb-12">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-4">
            Four pillars of real delegation
          </h2>
          <p className="text-text-secondary-light dark:text-text-secondary text-base sm:text-lg max-w-2xl mx-auto">
            Practical capability without sacrificing privacy or system security.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Pillar 1: Own PC & Robobrowser */}
          <div className={`${cardClass} flex flex-col justify-between`}>
            <div>
              <div className="w-12 h-12 rounded-xl bg-accent-muted text-accent flex items-center justify-center mb-5">
                <TerminalIcon className="w-6 h-6" />
              </div>
              <h3 className="font-display text-xl font-bold mb-3">
                1. Own GPU Linux PC & robobrowser: live dashboards & compute
              </h3>
              <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed mb-4">
                When Mentat needs to do real work, it spins up an isolated cloud
                Linux environment with a dedicated GPU, terminal, robobrowser,
                and developer tools.
              </p>
              <ul className="space-y-2.5 text-sm text-text-secondary-light dark:text-text-secondary">
                <li className="flex items-start gap-2">
                  <CheckIcon className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                  <span>
                    <strong>Builds live web dashboards:</strong>{" "}
                    Tell Mentat to track client pipelines, finances, or metrics.
                    It writes the code, connects a database, and gives you a
                    live URL.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckIcon className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                  <span>
                    <strong>Robobrowser automation:</strong>{" "}
                    Navigates portals, completes web forms, and scrapes supplier
                    data across websites autonomously.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckIcon className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                  <span>
                    <strong>Runs 24/7 in the cloud:</strong>{" "}
                    Keeps working when your laptop is closed, running persistent
                    background jobs without installing local software.
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Pillar 2: Phone & WhatsApp */}
          <div className={`${cardClass} flex flex-col justify-between`}>
            <div>
              <div className="w-12 h-12 rounded-xl bg-accent-muted text-accent flex items-center justify-center mb-5">
                <PhoneCallIcon className="w-6 h-6" />
              </div>
              <h3 className="font-display text-xl font-bold mb-3">
                2. Dedicated phone & WhatsApp lines
              </h3>
              <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed mb-4">
                Mentat gets its own phone number and its own WhatsApp line,
                letting it communicate directly with you and external contacts.
              </p>
              <ul className="space-y-2.5 text-sm text-text-secondary-light dark:text-text-secondary">
                <li className="flex items-start gap-2">
                  <CheckIcon className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                  <span>
                    <strong>Real duplex phone calls:</strong>{" "}
                    Call Mentat while driving or receive outbound audio
                    briefings every morning over standard telephone lines.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckIcon className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                  <span>
                    <strong>Proactive messaging with its own number:</strong>
                    {" "}
                    It texts contacts, vendors, and clients directly to confirm
                    times and follow up, keeping your personal SIM private.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckIcon className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                  <span>
                    <strong>WhatsApp direct & group chats:</strong>{" "}
                    Send voice notes and texts. Add Mentat to shared groups
                    where it tracks deliverables, quotes messages, and adds
                    emoji reactions.
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Pillar 3: Safescript for Proactive Actions */}
          <div className={`${cardClass} flex flex-col justify-between`}>
            <div>
              <div className="w-12 h-12 rounded-xl bg-accent-muted text-accent flex items-center justify-center mb-5">
                <CalendarIcon className="w-6 h-6" />
              </div>
              <h3 className="font-display text-xl font-bold mb-3">
                3. Safescript: cheap, safe proactive actions
              </h3>
              <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed mb-4">
                Most assistants either sit idle waiting for commands or burn
                hundreds of dollars polling raw LLMs on noisy inboxes. Mentat
                uses a three-tier pipeline.
              </p>
              <ul className="space-y-2.5 text-sm text-text-secondary-light dark:text-text-secondary">
                <li className="flex items-start gap-2">
                  <CheckIcon className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                  <span>
                    <strong>Zero-privilege edge sandbox:</strong>{" "}
                    Safescript runs with no shell access and static network
                    allowlists, neutralizing prompt injection attempts from
                    untrusted emails.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckIcon className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                  <span>
                    <strong>100x cheaper decision models:</strong>{" "}
                    Evaluates incoming alerts with instant decision classifiers,
                    dropping 95% of inbox noise for fractions of a cent.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckIcon className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                  <span>
                    <strong>Targeted LLM wake-up:</strong>{" "}
                    The full agent only wakes up when human action is needed,
                    delivering a draft to your WhatsApp ready for one-tap
                    approval.
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Pillar 4: Security & Approvals */}
          <div className={`${cardClass} flex flex-col justify-between`}>
            <div>
              <div className="w-12 h-12 rounded-xl bg-accent-muted text-accent flex items-center justify-center mb-5">
                <ShieldIcon className="w-6 h-6" />
              </div>
              <h3 className="font-display text-xl font-bold mb-3">
                4. Administrator approvals & egress-locked secrets
              </h3>
              <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed mb-4">
                Mentat gives you full delegative capability while keeping
                credentials and system boundaries protected by design.
              </p>
              <ul className="space-y-2.5 text-sm text-text-secondary-light dark:text-text-secondary">
                <li className="flex items-start gap-2">
                  <CheckIcon className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                  <span>
                    <strong>1-tap WhatsApp approvals:</strong>{" "}
                    If anyone in a shared group requests sensitive operations or
                    VM creation, execution pauses until you approve on WhatsApp.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckIcon className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                  <span>
                    <strong>Egress-locked credentials:</strong>{" "}
                    API keys are encrypted and injected at the network proxy.
                    The model structurally cannot see or leak plaintext keys.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckIcon className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                  <span>
                    <strong>Selective user boundaries:</strong>{" "}
                    Configure whether Mentat acts as a shared team member or
                    enforces strict per-user isolation without cross-thread
                    contamination.
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* A Day With Mentat */}
      <section id="day" className="py-16 px-4 sm:px-6 max-w-4xl mx-auto w-full">
        <div className="text-center mb-12">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-4">
            A day with Mentat
          </h2>
          <p className="text-text-secondary-light dark:text-text-secondary text-base sm:text-lg">
            How people use it to run their business and schedule.
          </p>
        </div>

        <div className="relative border-l-2 border-border-subtle-light dark:border-border-subtle ml-4 sm:ml-8 pl-6 sm:pl-8 space-y-10 text-left">
          <div className="relative">
            <div className="absolute -left-[31px] sm:-left-[39px] top-1.5 w-4 h-4 rounded-full bg-accent border-4 border-surface-light dark:border-surface" />
            <div className="text-xs font-semibold uppercase tracking-wider text-accent-light dark:text-accent mb-1">
              8:00 AM · Morning phone call
            </div>
            <h4 className="font-semibold text-base mb-1">
              Audio run-down of your day
            </h4>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
              Mentat calls your phone with a quick two-minute audio run-down of
              your meetings, open priorities, and travel weather while you get
              ready.
            </p>
          </div>

          <div className="relative">
            <div className="absolute -left-[31px] sm:-left-[39px] top-1.5 w-4 h-4 rounded-full bg-accent border-4 border-surface-light dark:border-surface" />
            <div className="text-xs font-semibold uppercase tracking-wider text-accent-light dark:text-accent mb-1">
              10:30 AM · Voice note on WhatsApp
            </div>
            <h4 className="font-semibold text-base mb-1">
              On-demand business dashboard
            </h4>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
              You send a voice note: "Build me a dashboard to track our
              consulting clients, active milestones, and revenue." Mentat opens
              its cloud machine, builds the app, deploys it to a secure link,
              and sends you the live URL.
            </p>
          </div>

          <div className="relative">
            <div className="absolute -left-[31px] sm:-left-[39px] top-1.5 w-4 h-4 rounded-full bg-accent border-4 border-surface-light dark:border-surface" />
            <div className="text-xs font-semibold uppercase tracking-wider text-accent-light dark:text-accent mb-1">
              1:15 PM · Quick WhatsApp text
            </div>
            <h4 className="font-semibold text-base mb-1">
              Calendar rescheduling
            </h4>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
              You text Mentat between calls: "Move my 3 PM with David to
              Thursday at 11 AM and text him a confirmation." Done in seconds.
            </p>
          </div>

          <div className="relative">
            <div className="absolute -left-[31px] sm:-left-[39px] top-1.5 w-4 h-4 rounded-full bg-accent border-4 border-surface-light dark:border-surface" />
            <div className="text-xs font-semibold uppercase tracking-wider text-accent-light dark:text-accent mb-1">
              4:00 PM · Automated cloud execution
            </div>
            <h4 className="font-semibold text-base mb-1">
              Vendor metrics & data scrape
            </h4>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
              Mentat runs a scheduled script on its cloud computer, logs into
              three vendor portals, extracts pricing changes, and posts a clean
              bulleted brief to your WhatsApp.
            </p>
          </div>

          <div className="relative">
            <div className="absolute -left-[31px] sm:-left-[39px] top-1.5 w-4 h-4 rounded-full bg-accent border-4 border-surface-light dark:border-surface" />
            <div className="text-xs font-semibold uppercase tracking-wider text-accent-light dark:text-accent mb-1">
              7:30 PM · Family or team group chat
            </div>
            <h4 className="font-semibold text-base mb-1">
              Coordination and task check-off
            </h4>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
              In your shared WhatsApp group, Mentat marks finished tasks as
              completed and reminds the group about upcoming plans.
            </p>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className={sectionClass}>
        <div className="text-center mb-12">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-4">
            How it compares
          </h2>
          <p className="text-text-secondary-light dark:text-text-secondary text-base sm:text-lg">
            Architectural difference between OpenClaw, Hermes, Meta Muse,
            Instinct, and Mentat.
          </p>
        </div>

        <div className="overflow-x-auto rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised shadow-sm">
          <table className="w-full text-left border-collapse text-sm">
            <thead>
              <tr className="border-b border-border-subtle-light dark:border-border-subtle bg-surface-overlay-light dark:bg-surface-overlay">
                <th className="p-4 sm:p-5 font-semibold text-left min-w-[200px]">
                  Capability
                </th>
                <th className="p-4 sm:p-5 font-semibold text-center text-text-secondary-light dark:text-text-secondary min-w-[120px]">
                  OpenClaw / Hermes
                </th>
                <th className="p-4 sm:p-5 font-semibold text-center text-text-secondary-light dark:text-text-secondary min-w-[110px]">
                  Meta Muse
                </th>
                <th className="p-4 sm:p-5 font-semibold text-center text-text-secondary-light dark:text-text-secondary min-w-[110px]">
                  Instinct
                </th>
                <th className="p-4 sm:p-5 font-semibold text-center text-accent-light dark:text-accent bg-accent/5 min-w-[150px]">
                  Mentat by prompt2bot
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle-light dark:divide-border-subtle">
              <tr>
                <td className="p-4 sm:p-5 font-medium text-left">
                  Dedicated cloud Linux PC with GPU
                </td>
                <td className="p-4 sm:p-5 text-center">
                  <span className="text-text-tertiary-light dark:text-text-tertiary font-bold text-base">
                    ✕<sup className="text-[11px] text-accent font-semibold ml-0.5">
                      *1
                    </sup>
                  </span>
                </td>
                <td className="p-4 sm:p-5 text-center">
                  <span className="text-text-tertiary-light dark:text-text-tertiary font-bold text-base">
                    ✕
                  </span>
                </td>
                <td className="p-4 sm:p-5 text-center">
                  <span className="text-text-tertiary-light dark:text-text-tertiary font-bold text-base">
                    ✕
                  </span>
                </td>
                <td className="p-4 sm:p-5 text-center bg-accent/5">
                  <span className="text-emerald-500 font-bold text-base">
                    ✓
                  </span>
                </td>
              </tr>
              <tr>
                <td className="p-4 sm:p-5 font-medium text-left">
                  Autonomous robobrowser
                </td>
                <td className="p-4 sm:p-5 text-center">
                  <span className="text-emerald-500 font-bold text-base">
                    ✓<sup className="text-[11px] text-accent font-semibold ml-0.5">
                      *2
                    </sup>
                  </span>
                </td>
                <td className="p-4 sm:p-5 text-center">
                  <span className="text-emerald-500 font-bold text-base">
                    ✓
                  </span>
                </td>
                <td className="p-4 sm:p-5 text-center">
                  <span className="text-text-tertiary-light dark:text-text-tertiary font-bold text-base">
                    ✕
                  </span>
                </td>
                <td className="p-4 sm:p-5 text-center bg-accent/5">
                  <span className="text-emerald-500 font-bold text-base">
                    ✓
                  </span>
                </td>
              </tr>
              <tr>
                <td className="p-4 sm:p-5 font-medium text-left">
                  Dedicated telephone line (real calls)
                </td>
                <td className="p-4 sm:p-5 text-center">
                  <span className="text-text-tertiary-light dark:text-text-tertiary font-bold text-base">
                    ✕
                  </span>
                </td>
                <td className="p-4 sm:p-5 text-center">
                  <span className="text-text-tertiary-light dark:text-text-tertiary font-bold text-base">
                    ✕
                  </span>
                </td>
                <td className="p-4 sm:p-5 text-center">
                  <span className="text-emerald-500 font-bold text-base">
                    ✓<sup className="text-[11px] text-accent font-semibold ml-0.5">
                      *3
                    </sup>
                  </span>
                </td>
                <td className="p-4 sm:p-5 text-center bg-accent/5">
                  <span className="text-emerald-500 font-bold text-base">
                    ✓
                  </span>
                </td>
              </tr>
              <tr>
                <td className="p-4 sm:p-5 font-medium text-left">
                  Dedicated WhatsApp line & SMS
                </td>
                <td className="p-4 sm:p-5 text-center">
                  <span className="text-text-tertiary-light dark:text-text-tertiary font-bold text-base">
                    ✕<sup className="text-[11px] text-accent font-semibold ml-0.5">
                      *4
                    </sup>
                  </span>
                </td>
                <td className="p-4 sm:p-5 text-center">
                  <span className="text-text-tertiary-light dark:text-text-tertiary font-bold text-base">
                    ✕
                  </span>
                </td>
                <td className="p-4 sm:p-5 text-center">
                  <span className="text-emerald-500 font-bold text-base">
                    ✓
                  </span>
                </td>
                <td className="p-4 sm:p-5 text-center bg-accent/5">
                  <span className="text-emerald-500 font-bold text-base">
                    ✓
                  </span>
                </td>
              </tr>
              <tr>
                <td className="p-4 sm:p-5 font-medium text-left">
                  Proactive outreach to others with own number
                </td>
                <td className="p-4 sm:p-5 text-center">
                  <span className="text-text-tertiary-light dark:text-text-tertiary font-bold text-base">
                    ✕
                  </span>
                </td>
                <td className="p-4 sm:p-5 text-center">
                  <span className="text-text-tertiary-light dark:text-text-tertiary font-bold text-base">
                    ✕
                  </span>
                </td>
                <td className="p-4 sm:p-5 text-center">
                  <span className="text-emerald-500 font-bold text-base">
                    ✓<sup className="text-[11px] text-accent font-semibold ml-0.5">
                      *5
                    </sup>
                  </span>
                </td>
                <td className="p-4 sm:p-5 text-center bg-accent/5">
                  <span className="text-emerald-500 font-bold text-base">
                    ✓
                  </span>
                </td>
              </tr>
              <tr>
                <td className="p-4 sm:p-5 font-medium text-left">
                  Safescript edge filtering (100x cheaper)
                </td>
                <td className="p-4 sm:p-5 text-center">
                  <span className="text-text-tertiary-light dark:text-text-tertiary font-bold text-base">
                    ✕
                  </span>
                </td>
                <td className="p-4 sm:p-5 text-center">
                  <span className="text-text-tertiary-light dark:text-text-tertiary font-bold text-base">
                    ✕
                  </span>
                </td>
                <td className="p-4 sm:p-5 text-center">
                  <span className="text-text-tertiary-light dark:text-text-tertiary font-bold text-base">
                    ✕
                  </span>
                </td>
                <td className="p-4 sm:p-5 text-center bg-accent/5">
                  <span className="text-emerald-500 font-bold text-base">
                    ✓<sup className="text-[11px] text-accent font-semibold ml-0.5">
                      *6
                    </sup>
                  </span>
                </td>
              </tr>
              <tr>
                <td className="p-4 sm:p-5 font-medium text-left">
                  Egress-locked credentials (keys never dump)
                </td>
                <td className="p-4 sm:p-5 text-center">
                  <span className="text-text-tertiary-light dark:text-text-tertiary font-bold text-base">
                    ✕
                  </span>
                </td>
                <td className="p-4 sm:p-5 text-center">
                  <span className="text-emerald-500 font-bold text-base">
                    ✓
                  </span>
                </td>
                <td className="p-4 sm:p-5 text-center">
                  <span className="text-text-tertiary-light dark:text-text-tertiary font-bold text-base">
                    ✕
                  </span>
                </td>
                <td className="p-4 sm:p-5 text-center bg-accent/5">
                  <span className="text-emerald-500 font-bold text-base">
                    ✓
                  </span>
                </td>
              </tr>
              <tr>
                <td className="p-4 sm:p-5 font-medium text-left">
                  1-tap WhatsApp owner approval gates
                </td>
                <td className="p-4 sm:p-5 text-center">
                  <span className="text-text-tertiary-light dark:text-text-tertiary font-bold text-base">
                    ✕
                  </span>
                </td>
                <td className="p-4 sm:p-5 text-center">
                  <span className="text-text-tertiary-light dark:text-text-tertiary font-bold text-base">
                    ✕
                  </span>
                </td>
                <td className="p-4 sm:p-5 text-center">
                  <span className="text-text-tertiary-light dark:text-text-tertiary font-bold text-base">
                    ✕
                  </span>
                </td>
                <td className="p-4 sm:p-5 text-center bg-accent/5">
                  <span className="text-emerald-500 font-bold text-base">
                    ✓
                  </span>
                </td>
              </tr>
              <tr>
                <td className="p-4 sm:p-5 font-medium text-left">
                  Available today (no waitlist)
                </td>
                <td className="p-4 sm:p-5 text-center">
                  <span className="text-emerald-500 font-bold text-base">
                    ✓
                  </span>
                </td>
                <td className="p-4 sm:p-5 text-center">
                  <span className="text-emerald-500 font-bold text-base">
                    ✓<sup className="text-[11px] text-accent font-semibold ml-0.5">
                      *7
                    </sup>
                  </span>
                </td>
                <td className="p-4 sm:p-5 text-center">
                  <span className="text-text-tertiary-light dark:text-text-tertiary font-bold text-base">
                    ✕<sup className="text-[11px] text-accent font-semibold ml-0.5">
                      *8
                    </sup>
                  </span>
                </td>
                <td className="p-4 sm:p-5 text-center bg-accent/5">
                  <span className="text-emerald-500 font-bold text-base">
                    ✓
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Stars and Footnotes */}
        <div className="mt-6 p-4 sm:p-6 rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised text-xs text-text-secondary-light dark:text-text-secondary text-left space-y-2">
          <div className="font-semibold text-text-primary-light dark:text-text-primary text-xs uppercase tracking-wider mb-2">
            Notes & Context
          </div>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
            <div>
              <span className="font-bold text-accent mr-1.5">*1</span>
              Runs locally or on manual VPS, stopping when computer sleeps.
            </div>
            <div>
              <span className="font-bold text-accent mr-1.5">*2</span>
              Browser automation runs on personal machine display.
            </div>
            <div>
              <span className="font-bold text-accent mr-1.5">*3</span>
              Concierge calling feature to businesses on user behalf.
            </div>
            <div>
              <span className="font-bold text-accent mr-1.5">*4</span>
              Self-hosted WhatsApp Web session prone to disconnects and bans.
            </div>
            <div>
              <span className="font-bold text-accent mr-1.5">*5</span>
              Calls businesses, but has no dedicated user-owned line for direct
              messaging.
            </div>
            <div>
              <span className="font-bold text-accent mr-1.5">*6</span>
              Safescript edge sandboxes and decision models drop 95% noise at
              1/100th the cost.
            </div>
            <div>
              <span className="font-bold text-accent mr-1.5">*7</span>
              US consumer rollout only, locked into Meta ecosystem.
            </div>
            <div>
              <span className="font-bold text-accent mr-1.5">*8</span>
              Requires private invite code with unpublished pricing.
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className={sectionClass}>
        <PricingSection />
      </section>

      {/* Testimonials Section */}
      <TestimonialsSection />

      {/* FAQ Section */}
      <FaqSection />

      {/* Bottom CTA Banner */}
      <section className="py-20 px-4 sm:px-6 max-w-4xl mx-auto w-full text-center">
        <div className="p-10 sm:p-14 rounded-3xl bg-gradient-to-b from-accent-light/10 via-surface-raised-light to-surface-raised-light dark:from-accent/15 dark:via-surface-raised dark:to-surface-raised border border-accent/20 shadow-xl space-y-6">
          <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight max-w-2xl mx-auto leading-tight">
            An assistant with tools of its own.
          </h2>
          <p className="text-base sm:text-lg text-text-secondary-light dark:text-text-secondary max-w-xl mx-auto leading-relaxed">
            Its own robobrowser, GPU Linux PC, phone line, and WhatsApp. Running
            24/7 with Safescript edge protection.
          </p>
          <div className="pt-2 flex items-center justify-center">
            <button
              type="button"
              onClick={() => setShowSetup(true)}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-9 py-4 rounded-xl bg-accent hover:bg-accent-hover text-white font-semibold text-base shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 cursor-pointer"
            >
              <span>Launch Mentat in one click</span>
              <ArrowRightIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {showSetup && (
        <MentatSetupModal
          isOpen={showSetup}
          onClose={() => setShowSetup(false)}
        />
      )}
    </div>
  );
};
